// TTQS engine — device-local per-fact mastery + spaced review.
// No accounts. Feeds the existing masteryEngine + passport XP.
// Fact states: not_seen → introduced → practising → quick_recall → retained
// (with needs_review demotion on a slip).

import { FACT_BY_ID, ALL_FACTS, factsForYear } from "@/data/ttqsFacts";
import { recordAttempt } from "@/lib/masteryEngine";
import { addStars } from "@/lib/progress";
import { track } from "@/lib/track";

const KEY = "mwq_ttqs_v1";
const QUICK_MS = 3500;                 // fast enough to count as fluent recall
// Spaced-review ladder (ms). A fast-correct answer climbs a rung; a slip drops.
const LADDER = [0, 1, 3, 7, 14, 30].map((d) => d * 24 * 3600 * 1000);

function _load() {
  try { return JSON.parse(localStorage.getItem(KEY) || "{}"); } catch { return {}; }
}
function _save(s) { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* ignore */ } }

function _blank() {
  return { state: "not_seen", correct: 0, wrong: 0, streak: 0, rung: 0,
           lastMs: [], nextReviewAt: 0, lastSeen: 0, hintUsed: 0 };
}

export function getFactState(id) {
  const s = _load();
  return s[id] || _blank();
}

function _median(arr) {
  if (!arr.length) return null;
  const a = [...arr].sort((x, y) => x - y);
  return a[Math.floor(a.length / 2)];
}

const ORDER = ["not_seen", "introduced", "practising", "quick_recall", "retained"];

export function introduce(id) {
  const s = _load();
  const f = s[id] || _blank();
  if (f.state === "not_seen") { f.state = "introduced"; f.lastSeen = Date.now(); s[id] = f; _save(s); }
}

export function recordAnswer(id, { correct, ms = 9999, hintUsed = false }) {
  const s = _load();
  const f = s[id] || _blank();
  const fast = correct && ms <= QUICK_MS && !hintUsed;
  const now = Date.now();
  f.lastSeen = now;
  if (hintUsed) f.hintUsed += 1;
  f.lastMs = [...(f.lastMs || []), ms].slice(-8);
  if (correct) {
    f.correct += 1; f.streak = f.streak >= 0 ? f.streak + 1 : 1;
    if (fast) { f.rung = Math.min(LADDER.length - 1, f.rung + 1); }
    // Promote conservatively — evidence, not one lucky hit.
    if (f.state === "not_seen" || f.state === "introduced") f.state = "practising";
    else if (f.state === "practising" && f.streak >= 2 && fast) f.state = "quick_recall";
    else if (f.state === "quick_recall" && fast && f.correct >= 5) f.state = "retained";
  } else {
    f.wrong += 1; f.streak = f.streak <= 0 ? f.streak - 1 : -1;
    f.rung = Math.max(0, f.rung - 2);                 // a slip shortens the interval
    if (f.state === "retained" || f.state === "quick_recall") f.state = "needs_review";
    else if (f.state === "not_seen") f.state = "introduced";
  }
  f.nextReviewAt = now + LADDER[Math.min(f.rung, LADDER.length - 1)];
  s[id] = f; _save(s);
  // Feed the wider mastery + passport.
  const fact = FACT_BY_ID[id];
  if (fact) { try { recordAttempt(`ttqs-x${fact.table}`, { correct, firstTry: f.correct + f.wrong === 1, strand: "times-tables" }); } catch { /* ignore */ } }
  track("ttqs_fact_answered", { correct, fast });
  return f;
}

export function awardXp(n, reason) { try { addStars(n, reason); } catch { /* ignore */ } }

// ── selection ─────────────────────────────────────────────────────────
export function dueForReview(year, now = Date.now()) {
  const s = _load();
  return factsForYear(year).filter((f) => { const st = s[f.id]; return st && st.state !== "not_seen" && st.nextReviewAt && st.nextReviewAt <= now; });
}

export function weakFacts(year) {
  const s = _load();
  return factsForYear(year).filter((f) => {
    const st = s[f.id]; if (!st) return false;
    const seen = st.correct + st.wrong;
    const slow = _median(st.lastMs) > QUICK_MS;
    return st.state === "needs_review" || (seen >= 2 && st.wrong / seen > 0.3) || (st.state === "practising" && slow);
  });
}

function _shuffle(a) { return [...a].sort(() => Math.random() - 0.5); }

// FLUENCY SPRINT: a few weak/new facts + due reviews + some secure for momentum.
export function buildFluencySet(year, n = 12) {
  const s = _load();
  const due = dueForReview(year);
  const weak = weakFacts(year);
  const pool = factsForYear(year);
  const newish = pool.filter((f) => { const st = s[f.id]; return !st || st.state === "introduced" || st.state === "practising"; });
  const secure = pool.filter((f) => { const st = s[f.id]; return st && (st.state === "quick_recall" || st.state === "retained"); });
  const picks = [];
  const push = (arr, k) => { for (const f of _shuffle(arr)) { if (picks.length >= k) break; if (!picks.find((p) => p.id === f.id)) picks.push(f); } };
  push(weak, Math.ceil(n * 0.35));
  push(due, Math.ceil(n * 0.6));
  push(newish, Math.ceil(n * 0.85));
  push(secure, Math.ceil(n * 0.95));   // momentum
  push(pool, n);                        // top up
  return _shuffle(picks).slice(0, n);
}

// FACT REPAIR: recently-failed / needs-review facts, with their family links.
export function buildRepairSet(year, n = 8) {
  const s = _load();
  const failed = factsForYear(year)
    .filter((f) => { const st = s[f.id]; return st && (st.state === "needs_review" || (st.wrong > 0 && st.streak < 0)); })
    .sort((a, b) => (s[b.id].lastSeen || 0) - (s[a.id].lastSeen || 0));
  return failed.slice(0, n);
}

// TEACH QUEST: new-focus facts for the year that the child hasn't met.
export function buildTeachSet(year, focusTables, n = 3) {
  const s = _load();
  const pool = factsForYear(year).filter((f) => f.op === "multiplication" &&
    (focusTables.length ? (focusTables.includes(f.a) || focusTables.includes(f.b)) : true));
  const fresh = pool.filter((f) => { const st = s[f.id]; return !st || st.state === "not_seen" || st.state === "introduced"; });
  return _shuffle(fresh.length ? fresh : pool).slice(0, n);
}

// ── summary for the hub ────────────────────────────────────────────────
export function summary(year) {
  const s = _load();
  const pool = factsForYear(year);
  let quick = 0, retained = 0, practising = 0;
  pool.forEach((f) => { const st = s[f.id]; if (!st) return; if (st.state === "retained") retained += 1; else if (st.state === "quick_recall") quick += 1; else if (st.state === "practising" || st.state === "needs_review") practising += 1; });
  return { total: pool.length, quick, retained, practising, due: dueForReview(year).length };
}

export const QUICK_MS_THRESHOLD = QUICK_MS;

// ── Migration v2 ─────────────────────────────────────────────────────────
// Legacy devices may carry a big backlog of overdue rung-0 reviews that would
// all fire at once. v2 deterministically staggers those over 0–72h using a
// stable per-fact seed. Evidence is preserved (only nextReviewAt shifts); no
// artificial promotion; idempotent via mwq_ttqs_migration_version.
const MIG_KEY = "mwq_ttqs_migration_version";

function _hashId(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) { h = (h * 31 + str.charCodeAt(i)) | 0; }
  return Math.abs(h);
}

export function migrationVersion() {
  try { return parseInt(localStorage.getItem(MIG_KEY) || "0", 10) || 0; } catch { return 0; }
}

export function runMigrationV2(now = Date.now()) {
  if (migrationVersion() >= 2) return { migrated: 0, skipped: true };
  const s = _load();
  let migrated = 0;
  for (const id of Object.keys(s)) {
    const f = s[id];
    if (!f || f.state === "not_seen") continue;
    const overdue = f.nextReviewAt && f.nextReviewAt <= now;
    if (f.rung === 0 && overdue) {
      // Stable 0..72h stagger from the fact id — same device, same result.
      const hours = _hashId(id) % 73;            // 0..72
      f.nextReviewAt = now + hours * 3600 * 1000; // evidence untouched otherwise
      migrated += 1;
    }
  }
  _save(s);
  try { localStorage.setItem(MIG_KEY, "2"); } catch { /* ignore */ }
  try { track("ttqs_migration", { version: 2, migrated }); } catch { /* ignore */ }
  return { migrated, skipped: false };
}

// ── Fact World stats (GLOBAL, across all years) — drives map unlocks ──
export function worldStats(now = Date.now()) {
  const s = _load();
  let seen = 0, seenMult = 0, retained = 0, quick = 0, weak = 0, due = 0;
  for (const f of ALL_FACTS) {
    const st = s[f.id];
    if (!st || st.state === "not_seen") continue;
    seen += 1;
    if (f.op === "multiplication") seenMult += 1;
    if (st.state === "retained") retained += 1;
    if (st.state === "quick_recall") quick += 1;
    const total = st.correct + st.wrong;
    const slow = _median(st.lastMs) > QUICK_MS;
    if (st.state === "needs_review" || (total >= 2 && st.wrong / total > 0.3) || (st.state === "practising" && slow)) weak += 1;
    if (st.nextReviewAt && st.nextReviewAt <= now) due += 1;
  }
  return { seen, seenMult, retained, quick, weak, due };
}
